/**
 * useBlitzPlayback - Custom hook for the Blitz player (v0.9.0-rc2 contract).
 *
 * Consumes the flat per-chapter contract introduced by spec 010:
 *   chapter = { id, title, order, length, summary: string|null, key_points: KeyPoint[], summary_audio_url: string|null }
 *   KeyPoint = { id, order, title, text, audio_url: string|null }
 *
 * The hook builds a 2-level flat playback queue per chapter (revised
 * 2026-05-18 — the user-facing levels are now `chapter` and `point`;
 * the legacy `L1` / `L2` tags are gone):
 *   - chapter row (`level: 'chapter'`) — title = chapter.title;
 *                                        body  = chapter.summary;
 *                                        audio = chapter.summary_audio_url
 *   - point row   (`level: 'point'`)   — title = kp.title;
 *                                        body  = kp.text;
 *                                        audio = kp.audio_url
 *
 * The full-chapter narration (`/chapter/{id}.mp3`) is intentionally NOT in
 * the Blitz queue; the player surfaces it via a "Listen to full chapter"
 * link that navigates back to the Chapters page.
 *
 * Items with audioUrl == null are kept in the queue (so the user can still
 * navigate to and read their text) but auto-advance skips them. See
 * specs/010-processing-pipeline/data-model.md §"Validity Rules".
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { getConfig } from '../config';
import { getAccessToken } from '../utils/auth';
import * as repository from '../utils/offline/repository';
import { savePosition, restorePosition } from '../utils/blitzStorage';

const trimSlashes = (s) => String(s || '').replace(/\/+$/, '');

/**
 * Build a 2-level flat playback queue from v0.9.0-rc2 chapter records
 * (revised 2026-05-18 — `level` values are now `'chapter'` and `'point'`).
 *
 *   chapter row (`level: 'chapter'`, `type: 'chapter_summary'`)
 *     — one per chapter; title = chapter.title,
 *                        body  = chapter.summary,
 *                        audio = chapter.summary_audio_url (nullable mid-Blitz).
 *   point row   (`level: 'point'`, `type: 'key_point'`)
 *     — one per kp;     title = kp.title (fallback "Key point N"),
 *                        body  = kp.text,
 *                        audio = kp.audio_url (nullable mid-Blitz).
 *
 * The chapter is always represented in the queue even when its summary is
 * missing (so the user can still navigate to its key points). The full
 * chapter narration (`/chapter/{id}.mp3`) is NOT in the queue; the player
 * exposes it via the "Listen to full chapter" link instead.
 *
 * Key-point audio URL is derived from project/chapter/key-point IDs.
 * The server no longer returns audio_url on key_point objects.
 */
/** The shape a chapter takes when it has no readable detail yet. */
function emptyDetail(chapter, chapterId) {
  return {
    id: chapterId,
    title: chapter.title || null,
    order: chapter.order ?? 0,
    summary: null,
    summary_audio_url: null,
    key_points: [],
  };
}

/** Normalise a chapter-detail response into the flat shape the queue expects. */
function shapeDetail(chapter, body, chapterId) {
  const ch = (body && (body.chapter || body)) || {};
  return {
    id: chapterId,
    title: chapter.title || ch.title || null,
    order: chapter.order ?? ch.order ?? 0,
    summary: typeof ch.summary === 'string' ? ch.summary : null,
    summary_audio_url: typeof ch.summary_audio_url === 'string' ? ch.summary_audio_url : null,
    key_points: Array.isArray(ch.key_points) ? ch.key_points : [],
  };
}

/** Whatever this device holds for a chapter, or null. Never throws. */
async function readCachedDetail(fileId, chapterId) {
  try {
    return await repository.getText(fileId, chapterId);
  } catch (e) {
    return null; // no account bound, or the store is unavailable
  }
}

/**
 * Fetch one chapter's readable detail, preferring the server and falling back
 * to the copy on this device (FR-002).
 *
 * Never throws: a chapter with no detail yet and a chapter that cannot be
 * reached both render the same "not yet available" state they do online.
 */
async function fetchChapterDetail(chapter, { fileId, config, headers }) {
  // GET /projects/{id}/chapters returns ProjectChapterSummarySchema, whose id
  // field is `chapter_id`. Fall back to `id` for older shapes.
  const chapterId = chapter.chapter_id ?? chapter.id;
  try {
    const response = await axios.get(
      `${config.apiBaseUrl}/projects/${fileId}/chapters/${chapterId}`,
      { headers },
    );
    const shaped = shapeDetail(chapter, response && response.data, chapterId);
    // Best effort, isolated: a storage failure must not turn a successful
    // fetch into a chapter with no text.
    Promise.resolve()
      .then(() => repository.cacheText(fileId, chapterId, shaped))
      .catch((err) => console.warn('useBlitzPlayback: cache write failed', err));
    return shaped;
  } catch (e) {
    const cached = await readCachedDetail(fileId, chapterId);
    return cached ? { ...cached, id: chapterId } : emptyDetail(chapter, chapterId);
  }
}

export function buildPlaybackQueue(chapters, fileId, apiBaseUrl = '') {
  const queue = [];
  const base = trimSlashes(apiBaseUrl);
  if (!Array.isArray(chapters)) return queue;

  chapters.forEach((chapter, chapterIndex) => {
    if (!chapter || !chapter.id) return;
    const chapterId = chapter.id;
    const chapterTitle = chapter.title || `Chapter ${chapterIndex + 1}`;
    const summaryText = typeof chapter.summary === 'string' ? chapter.summary : '';
    const summaryAudio = chapter.summary_audio_url
      ? `${base}${chapter.summary_audio_url}`
      : (summaryText.trim() !== ''
        ? `${base}/projects/${fileId}/chapters/${chapterId}/summary.mp3`
        : null);

    queue.push({
      id: `${chapterId}-summary`,
      type: 'chapter_summary',
      level: 'chapter',
      chapterIndex,
      chapterId,
      chapterTitle,
      title: chapterTitle,
      text: summaryText,
      audioUrl: summaryAudio,
      breadcrumb: chapterTitle,
    });

    const rawKps = Array.isArray(chapter.key_points) ? chapter.key_points : [];
    const kps = rawKps
      .filter((kp) => kp && kp.id)
      .slice()
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
    kps.forEach((kp, kpIdx) => {
      const kpTitle =
        typeof kp.title === 'string' && kp.title.trim() !== ''
          ? kp.title
          : `Key point ${(Number.isFinite(Number(kp.order)) ? Number(kp.order) : kpIdx) + 1}`;
      queue.push({
        id: `${chapterId}-kp-${kp.id}`,
        type: 'key_point',
        level: 'point',
        chapterIndex,
        chapterId,
        chapterTitle,
        keyPointId: kp.id,
        order: Number.isFinite(Number(kp.order)) ? Number(kp.order) : kpIdx,
        title: kpTitle,
        text: typeof kp.text === 'string' ? kp.text : '',
        audioUrl: `${base}/projects/${fileId}/chapters/${chapterId}/key-points/${kp.id}.mp3`,
        breadcrumb: `${chapterTitle} > ${kpTitle}`,
      });
    });
  });

  return queue;
}

/**
 * Main playback hook.
 *
 * `progressSnapshot` is OPTIONAL. When supplied (e.g. from the parent
 * `<Blitz>` route's `useProgressPolling`), the hook watches the per-chapter
 * `has_key_points` flags and triggers a silent re-fetch of `chapter/{id}`
 * whenever the count of chapters with key-points changes — so the queue
 * grows mid-Phase-3 as new summaries / key points become available. The
 * re-fetch preserves `currentIndex`, `isPlaying` and the rest of the
 * playback state so the user is not interrupted (revised 2026-05-18).
 */
export function useBlitzPlayback(fileId, chapters, progressSnapshot = null) {
  const config = getConfig();

  // Playback state
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  // Audio ref for preloading
  const preloadRef = useRef(null);

  // Current item
  const currentItem = queue[currentIndex] || null;

  // Fetch each chapter's detail (which carries `summary` + `key_points` per
  // the v0.9.0-rc2 contract). The legacy `/summary?full_tree=true` endpoint
  // is GONE and MUST NOT be requested.
  //
  // `silent` (default `false`) controls whether the loading / error UI is
  // re-shown. Silent refreshes are used for mid-Phase-3 queue updates so
  // playback is not interrupted by a spinner.
  const fetchChapterDetails = useCallback(async (silent = false) => {
    if (!chapters || chapters.length === 0) {
      if (!silent) {
        setError('No chapters available');
        setIsLoading(false);
      }
      return;
    }

    if (!silent) {
      setIsLoading(true);
      setError(null);
    }

    try {
      const token = getAccessToken();
      const headers = { Authorization: `Bearer ${token}` };

      const detailed = await Promise.all(
        chapters.map((chapter) => fetchChapterDetail(chapter, { fileId, config, headers })),
      );

      const newQueue = buildPlaybackQueue(detailed, fileId, config.apiBaseUrl);
      if (newQueue.length === 0) {
        if (!silent) {
          setError('No playable content yet. Try again once processing finishes.');
          setIsLoading(false);
        }
        return;
      }
      setQueue(newQueue);

      // Only seek to the saved position on the INITIAL load. Silent
      // refreshes (mid-Phase-3 queue growth) must leave the current
      // playback position untouched.
      if (!silent) {
        const savedPos = restorePosition(fileId, newQueue.length);
        if (savedPos) {
          setCurrentIndex(savedPos.queueIndex);
        }
        setIsLoading(false);
      }
    } catch (e) {
      console.error('Failed to fetch chapter details:', e);
      if (!silent) {
        setError('Failed to load playback queue. Please try again.');
        setIsLoading(false);
      }
    }
  }, [chapters, config, fileId]);

  // Initialize on mount
  useEffect(() => {
    fetchChapterDetails();
  }, [fetchChapterDetails]);

  // Silent re-fetch when the progress snapshot reports that more chapters have
  // completed Phase 3. ProjectProgressResponse (v0.12.x) exposes per-file
  // counters in `files[]`, so we watch the total blitzed chapter count: when it
  // grows, new summaries / key points have become available and we refresh the
  // queue without interrupting playback.
  const readyChapterCount = useMemo(() => {
    const files = Array.isArray(progressSnapshot?.files) ? progressSnapshot.files : null;
    if (!files) return null;
    return files.reduce((sum, f) => sum + (f.blitzed_chapter_count || 0), 0);
  }, [progressSnapshot]);
  const prevReadyCountRef = useRef(null);
  useEffect(() => {
    if (readyChapterCount == null) return;
    if (isLoading) return; // Wait until the initial fetch finishes.
    if (prevReadyCountRef.current === null) {
      prevReadyCountRef.current = readyChapterCount;
      return;
    }
    if (readyChapterCount !== prevReadyCountRef.current) {
      prevReadyCountRef.current = readyChapterCount;
      fetchChapterDetails(true);
    }
  }, [readyChapterCount, isLoading, fetchChapterDetails]);

  // Preload next audio segment
  useEffect(() => {
    if (queue.length === 0 || currentIndex >= queue.length - 1) return;

    const nextItem = queue[currentIndex + 1];
    if (nextItem && nextItem.audioUrl) {
      const preloadAudio = new Audio();
      preloadAudio.preload = 'auto';
      preloadAudio.src = nextItem.audioUrl;
      preloadRef.current = preloadAudio;
    }

    return () => {
      if (preloadRef.current) {
        preloadRef.current.src = '';
        preloadRef.current = null;
      }
    };
  }, [currentIndex, queue]);

  // Save position when current index changes
  useEffect(() => {
    if (queue.length > 0 && currentIndex < queue.length) {
      savePosition(fileId, currentIndex, 0);
    }
  }, [fileId, currentIndex, queue.length]);

  // Playback controls
  const play = useCallback(() => {
    setIsPlaying(true);
    setAutoplayBlocked(false);
  }, []);

  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Advance to the next item that has a playable audioUrl. Key points whose
  // MP3 has not yet been rendered (audioUrl == null, mid-Blitz / partial
  // resume) are skipped during auto-advance but remain navigable via
  // jumpToIndex / jumpToKeyPoint so the user can still read their text.
  const next = useCallback(() => {
    for (let i = currentIndex + 1; i < queue.length; i++) {
      if (queue[i] && queue[i].audioUrl) {
        setCurrentIndex(i);
        setIsComplete(false);
        return;
      }
    }
    setIsComplete(true);
    setIsPlaying(false);
  }, [currentIndex, queue]);

  const prev = useCallback(() => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (queue[i] && queue[i].audioUrl) {
        setCurrentIndex(i);
        setIsComplete(false);
        return;
      }
    }
  }, [currentIndex, queue]);

  const jumpToChapter = useCallback((chapterIndex) => {
    const targetIndex = queue.findIndex(item => item.chapterIndex === chapterIndex);
    if (targetIndex !== -1) {
      setCurrentIndex(targetIndex);
      setIsComplete(false);
    }
  }, [queue]);

  const jumpToIndex = useCallback((index) => {
    if (index >= 0 && index < queue.length) {
      setCurrentIndex(index);
      setIsComplete(false);
    }
  }, [queue.length]);

  const retry = useCallback(() => {
    setError(null);
    fetchChapterDetails();
  }, [fetchChapterDetails]);

  // Jump to a specific key point in any chapter (rc2 per-key-point click-to-play).
  const jumpToKeyPoint = useCallback((keyPointId) => {
    const targetIndex = queue.findIndex((it) => it && it.type === 'key_point' && it.keyPointId === keyPointId);
    if (targetIndex !== -1) {
      setCurrentIndex(targetIndex);
      setIsComplete(false);
    }
  }, [queue]);

  // Chapter-level navigation: jump to the first item of the previous / next
  // chapter relative to the currently playing item. The 2-level queue
  // (chapter → point) collapses navigateUp / navigateDown to
  // chapter-prev / chapter-next.
  const navigateUp = useCallback(() => {
    if (!currentItem) return;
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (queue[i].chapterIndex < currentItem.chapterIndex) {
        let firstInPrev = i;
        while (firstInPrev > 0 && queue[firstInPrev - 1].chapterIndex === queue[i].chapterIndex) firstInPrev--;
        setCurrentIndex(firstInPrev);
        setIsComplete(false);
        return;
      }
    }
  }, [currentItem, currentIndex, queue]);

  const navigateDown = useCallback(() => {
    if (!currentItem) return;
    for (let i = currentIndex + 1; i < queue.length; i++) {
      if (queue[i].chapterIndex > currentItem.chapterIndex) {
        setCurrentIndex(i);
        setIsComplete(false);
        return;
      }
    }
  }, [currentItem, currentIndex, queue]);

  const canNavigateUp = useCallback(() => {
    if (!currentItem) return false;
    return queue.some((it, i) => i < currentIndex && it.chapterIndex < currentItem.chapterIndex);
  }, [currentItem, currentIndex, queue]);

  const canNavigateDown = useCallback(() => {
    if (!currentItem) return false;
    return queue.some((it, i) => i > currentIndex && it.chapterIndex > currentItem.chapterIndex);
  }, [currentItem, currentIndex, queue]);

  // Get unique chapters for navigation
  const chapterList = queue.reduce((acc, item) => {
    if (!acc.find(ch => ch.index === item.chapterIndex)) {
      acc.push({ index: item.chapterIndex, title: item.chapterTitle });
    }
    return acc;
  }, []);

  return {
    // State
    queue,
    currentIndex,
    currentItem,
    isPlaying,
    isLoading,
    error,
    isComplete,
    autoplayBlocked,
    chapterList,

    // Controls
    play,
    pause,
    next,
    prev,
    jumpToChapter,
    jumpToIndex,
    jumpToKeyPoint,
    navigateUp,
    navigateDown,
    canNavigateUp: canNavigateUp(),
    canNavigateDown: canNavigateDown(),
    retry,
    setIsPlaying,
    setAutoplayBlocked
  };
}

